import {AssetApiDto, CreateUpdateAssetApiDto} from "../../api/model/rest";
import {none, option, Option} from "ts-option";
import JsonConverter from "../../api/helper/JsonConverter";
import Attachment from "../attachment/Attachment";
import AssetSpecification from "./AssetSpecification";
import AssetSearch from "./AssetSearch";

export default class Asset {
	constructor(public readonly id: string,
		public readonly categoryId: string,
		public readonly manufacturer: Option<string>,
		public readonly model: string,
		public readonly price: number,
		public readonly secondaryPrice: number,
		public readonly rebate: Option<number>,
		public readonly type: Option<string>,
		public readonly disabled: boolean,
		public readonly attachments: Attachment[],
		public readonly specifications: AssetSpecification[],
		public readonly firstMerchantCode: Option<string>,
		public readonly secondMerchantCode: Option<string>,
		public readonly description: Option<string>,
		public readonly markup: number,
		public readonly tierLevel: TierLevel,
		public readonly rewardsPoints: number,
		public readonly keywords: string[],
		public readonly alertNote: Option<string>,
		public readonly warrantyCode: Option<string>,
		public readonly applianceShortCode: Option<string>) {}

	static fromJson(json: AssetApiDto): Asset {
		const attachment = json.attachments ? json.attachments.map(Attachment.fromJson) : [];
		const firstMerchantCode = json.firstMerchantCode ? option(json.firstMerchantCode) : none;
		const secondMerchantCode = json.secondMerchantCode ? option(json.secondMerchantCode) : none;
		return new Asset(json.id,
			json.categoryId,
			option(json.manufacturer),
			json.model,
			json.price,
			json.secondaryPrice,
			option(json.rebate),
			option(json.type),
			json.disabled,
			attachment,
			json.specifications.map(AssetSpecification.fromJson),
			firstMerchantCode,
			secondMerchantCode,
			option(json.description),
			json.markup,
			TierLevel[json.tier],
			json.points,
			json.keywords,
			option(json.alertNote),
			option(json.warrantyCode),
			option(json.applianceShortCode)
		);
	}

	toJson(): CreateUpdateAssetApiDto {
		const json: CreateUpdateAssetApiDto = {
			categoryId: this.categoryId,
			manufacturer: this.manufacturer.orNull,
			model: this.model,
			price: this.price,
			secondaryPrice: this.secondaryPrice,
			rebate: this.rebate.orNull,
			type: this.type.orNull,
			disabled: this.disabled,
			specifications: this.specifications,
			firstMerchantCode: this.firstMerchantCode.orNull,
			secondMerchantCode: this.secondMerchantCode.orNull,
			description: this.description.orNull,
			markup: this.markup,
			tier: this.tierLevel,
			points: this.rewardsPoints,
			keywords: this.keywords,
			alertNote: this.alertNote.orNull,
			warrantyCode: this.warrantyCode.orNull,
			applianceShortCode: this.applianceShortCode.orNull
		};
		return JsonConverter.removeEmptyFields(json);
	}

	toAssetSearch(): AssetSearch {
		return new AssetSearch(this.id, this.manufacturer, this.model, this.price * 100, this.secondaryPrice * 100,
			option(this.attachments[0]).map(a => a.id), this.categoryId, this.disabled, this.markup)
	}

	copy({
		id = this.id,
		categoryId = this.categoryId,
		manufacturer = this.manufacturer,
		model = this.model,
		price = this.price,
		secondaryPrice = this.secondaryPrice,
		rebate = this.rebate,
		type = this.type,
		disabled = this.disabled,
		attachments = this.attachments,
		specifications = this.specifications,
		firstMerchantCode = this.firstMerchantCode,
		secondMerchantCode = this.secondMerchantCode,
		description = this.description
	}) {
		return new Asset(id, categoryId, manufacturer, model, price, secondaryPrice, rebate, type, disabled, attachments,
			specifications,
			firstMerchantCode, secondMerchantCode, description, this.markup, this.tierLevel, this.rewardsPoints, this.keywords,
			this.alertNote, this.warrantyCode, this.applianceShortCode);
	}
}

export enum TierLevel {
	STANDARD = "STANDARD",
	SELECT = "SELECT",
	BONUS = "BONUS",
	HOT = "HOT"
}
